import React, { useState } from 'react';
import { Card, Button, Input, List, Avatar, Tag, Popconfirm, message } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { TextArea } = Input;

const NotesTab = ({ employeeId }) => {
  const [notes, setNotes] = useState([
    {
      id: 1,
      content: 'Employee showed excellent performance in Q4 2023. Recommended for promotion consideration.',
      createdBy: {
        name: 'John Manager',
        avatar: null,
      },
      createdAt: '2024-01-15T10:30:00',
      isPrivate: false,
      category: 'performance',
    },
    {
      id: 2,
      content: 'Completed leadership training program successfully.',
      createdBy: {
        name: 'HR Department',
        avatar: null,
      },
      createdAt: '2024-01-10T14:20:00',
      isPrivate: false,
      category: 'training',
    },
    {
      id: 3,
      content: 'Discussed career development goals. Employee interested in project management role.',
      createdBy: {
        name: 'Sarah HR',
        avatar: null,
      },
      createdAt: '2024-01-05T09:15:00',
      isPrivate: true,
      category: 'general',
    },
  ]);

  const [newNote, setNewNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const categoryColors = {
    performance: 'blue',
    training: 'green',
    disciplinary: 'red',
    general: 'default',
    achievement: 'gold',
  };

  const handleAddNote = () => {
    if (!newNote.trim()) {
      message.warning('Please enter a note');
      return;
    }

    const note = {
      id: Date.now(),
      content: newNote,
      createdBy: {
        name: 'Current User', // TODO: Get from auth state
        avatar: null,
      },
      createdAt: new Date().toISOString(),
      isPrivate: false,
      category: 'general',
    };

    setNotes([note, ...notes]);
    setNewNote('');
    setIsAdding(false);
    message.success('Note added successfully');
  };

  const handleDeleteNote = (id) => {
    setNotes(notes.filter(note => note.id !== id));
    message.success('Note deleted successfully');
  };

  const handleEditNote = (id) => {
    // TODO: Implement edit functionality
    message.info('Edit functionality coming soon');
  };

  if (!employeeId) {
    return null;
  }

  return (
    <div className="tab-pane fade" id="emp_notes">
      <div className="card">
        <div className="card-body">
          <div className="row mb-4">
            <div className="col-md-6">
              <h3 className="card-title">Employee Notes</h3>
              <p className="text-muted">Private notes and observations about the employee</p>
            </div>
            <div className="col-md-6 text-end">
              {!isAdding ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAdding(true)}
                >
                  Add Note
                </Button>
              ) : (
                <Button onClick={() => setIsAdding(false)}>
                  Cancel
                </Button>
              )}
            </div>
          </div>

          {/* Add Note Form */}
          {isAdding && (
            <Card className="mb-4">
              <TextArea
                rows={4}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Enter your note here..."
                maxLength={1000}
                showCount
              />
              <div className="mt-3 text-end">
                <Button
                  type="primary"
                  onClick={handleAddNote}
                >
                  Save Note
                </Button>
              </div>
            </Card>
          )}

          {/* Notes List */}
          <div className="notes-list">
            {notes.length === 0 ? (
              <Card>
                <div className="text-center py-5">
                  <p className="text-muted">No notes available</p>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsAdding(true)}
                  >
                    Add First Note
                  </Button>
                </div>
              </Card>
            ) : (
              <List
                itemLayout="vertical"
                dataSource={notes}
                renderItem={(note) => (
                  <List.Item
                    key={note.id}
                    actions={[
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditNote(note.id)}
                      >
                        Edit
                      </Button>,
                      <Popconfirm
                        title="Delete this note?"
                        description="This action cannot be undone."
                        onConfirm={() => handleDeleteNote(note.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        >
                          Delete
                        </Button>
                      </Popconfirm>,
                    ]}
                  >
                    <Card>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            icon={<UserOutlined />}
                            src={note.createdBy.avatar}
                          >
                            {note.createdBy.name.charAt(0)}
                          </Avatar>
                        }
                        title={
                          <div>
                            <span>{note.createdBy.name}</span>
                            <span className="text-muted ms-2" style={{ fontSize: '12px' }}>
                              {dayjs(note.createdAt).fromNow()}
                            </span>
                            {note.isPrivate && (
                              <Tag color="orange" className="ms-2" style={{ fontSize: '10px' }}>
                                Private
                              </Tag>
                            )}
                            <Tag
                              color={categoryColors[note.category]}
                              className="ms-1"
                              style={{ fontSize: '10px' }}
                            >
                              {note.category}
                            </Tag>
                          </div>
                        }
                        description={
                          <div className="mt-2">
                            {note.content}
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotesTab;
